import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, Camera, CheckCircle2, Loader2, RefreshCw, AlertCircle, Clock, Trash2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated, useSession } from '@/core/auth/SessionStore'
import { useFotosDoProjeto, useProjeto } from '@/core/db/daos'
import { SyncStatusIndicator } from '@/features/common/SyncStatusIndicator'
import { triggerForegroundSync } from '@/core/sync/foregroundSync'
import { deleteFoto } from '@/features/fotos/fotoRepo'
import type { OutFoto } from '@/core/db/entities'
import { cn } from '@/lib/cn'

/**
 * Lista de fotos PENDENTES (não-SYNCED) — refactor 2026-05-26:
 *
 *   - REMOVIDO: visualização de foto (thumbs + detalhe fullscreen). User
 *     pediu pra app focar SÓ no que falta enviar — fotos sincronizadas
 *     ficam no SIGA web e (no APK) também na galeria do telefone.
 *   - Cards verticais com status, metadados e ação retry/excluir.
 *   - Contador secundário "X já enviadas" pra dar feedback de progresso.
 *   - FAB central "Tirar foto" + botão "Sincronizar agora" no header.
 */
export function FotosListRoute() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()

  const projeto = useProjeto(session?.projetoAtualId)
  const feed = useFotosDoProjeto(session?.projetoAtualId)
  const pending = feed?.pending
  const syncedCount = feed?.syncedCount ?? 0

  if (!authed) return <Navigate to="/login" replace />
  if (session?.projetoAtualId == null) return <Navigate to="/" replace />

  function handleRefresh() {
    void triggerForegroundSync('fotos-list-refresh', { ignoreCooldown: true })
  }

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface truncate">Fotos pendentes</h1>
            <p className="text-xs text-on-surface-dim truncate">
              {projeto?.codigo ?? '—'} ·{' '}
              {pending == null
                ? 'Carregando…'
                : pending.length === 0
                  ? 'Nada pendente'
                  : `${pending.length} aguardando sync`}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleRefresh} aria-label="Sincronizar agora">
            <RefreshCw className="h-5 w-5" />
          </Button>
          <SyncStatusIndicator />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 pb-28 max-w-2xl mx-auto w-full">
        {pending == null ? (
          <div className="text-center text-on-surface-dim mt-12 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        ) : pending.length === 0 ? (
          <EmptyState syncedCount={syncedCount} />
        ) : (
          <>
            <ul className="space-y-2">
              {pending.map((f) => (
                <li key={f.id}>
                  <PendingFotoCard foto={f} />
                </li>
              ))}
            </ul>
            {syncedCount > 0 && (
              <p className="text-center text-xs text-on-surface-dim mt-6 inline-flex items-center justify-center gap-1 w-full">
                <CheckCircle2 className="h-3 w-3 text-ok" />
                {syncedCount} foto{syncedCount > 1 ? 's' : ''} já enviada
                {syncedCount > 1 ? 's' : ''} no SIGA
              </p>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate('/fotos/capture')}
        className="fixed bottom-6 right-6 z-20 h-16 w-16 rounded-full bg-accent text-bg-base shadow-xl flex items-center justify-center hover:opacity-90 active:opacity-80 transition-opacity"
        aria-label="Tirar foto"
      >
        <Camera className="h-7 w-7" />
      </button>
    </main>
  )
}

function EmptyState({ syncedCount }: { syncedCount: number }) {
  return (
    <div className="flex items-center justify-center min-h-[60svh]">
      <div className="text-center max-w-xs">
        <div className="h-16 w-16 mx-auto rounded-full bg-ok/10 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-ok" />
        </div>
        <p className="text-on-surface font-medium mt-4">Tudo sincronizado</p>
        <p className="text-sm text-on-surface-dim mt-2">
          {syncedCount > 0
            ? `${syncedCount} foto${syncedCount > 1 ? 's' : ''} no SIGA. Toque na câmera pra registrar a próxima.`
            : 'Toque na câmera pra registrar a primeira foto do projeto.'}
        </p>
      </div>
    </div>
  )
}

function PendingFotoCard({ foto }: { foto: OutFoto }) {
  const time = new Date(foto.capturedAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

  async function handleDelete() {
    if (!confirm('Excluir essa foto pendente? Não dá pra recuperar.')) return
    await deleteFoto(foto.id)
  }

  return (
    <div className={cn('card-surface px-3 py-3 flex items-stretch gap-3', statusBorderCls(foto.syncStatus))}>
      <div className="w-1 rounded-full" style={{ backgroundColor: statusColor(foto.syncStatus) }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <StatusBadge status={foto.syncStatus} />
          <span className="text-xs text-on-surface-dim">{time}</span>
        </div>
        <p className="text-sm font-medium text-on-surface mt-1 truncate">
          {foto.servicoExecutadoNome ?? '(sem serviço)'}
        </p>
        <p className="text-xs text-on-surface-dim truncate">
          {[foto.encarregadoNome, foto.equipeNome].filter(Boolean).join(' · ') || '—'}
        </p>
        {foto.lat != null && foto.lng != null && (
          <p className="text-[10px] text-on-surface-muted mt-1">
            GPS {foto.lat.toFixed(4)}, {foto.lng.toFixed(4)}
            {foto.accuracy ? ` ±${Math.round(foto.accuracy)}m` : ''}
          </p>
        )}
        {foto.syncStatus === 'FAILED' && foto.failureReason && (
          <p className="text-[10px] text-bad mt-1 truncate">Erro: {foto.failureReason}</p>
        )}
      </div>
      {foto.syncStatus !== 'SYNCING' && (
        <button
          type="button"
          onClick={handleDelete}
          className="px-2 text-bad hover:bg-bad/10 transition-colors flex items-center rounded"
          aria-label="Excluir"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: OutFoto['syncStatus'] }) {
  const map: Record<OutFoto['syncStatus'], { label: string; icon: JSX.Element; cls: string }> = {
    PENDING: { label: 'Aguardando', icon: <Clock className="h-3 w-3" />, cls: 'bg-warn/15 text-warn' },
    SYNCING: {
      label: 'Enviando',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
      cls: 'bg-info/15 text-info',
    },
    SYNCED: { label: 'Enviada', icon: <CheckCircle2 className="h-3 w-3" />, cls: 'bg-ok/15 text-ok' },
    FAILED: { label: 'Falhou', icon: <AlertCircle className="h-3 w-3" />, cls: 'bg-bad/15 text-bad' },
    // Estado do carreteiro; fotos nunca chegam nele (mantido pelo tipo compartilhado).
    REJEITADA: { label: 'Recusada', icon: <AlertCircle className="h-3 w-3" />, cls: 'bg-bad/15 text-bad' },
  }
  const m = map[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide',
        m.cls,
      )}
    >
      {m.icon}
      {m.label}
    </span>
  )
}

function statusColor(status: OutFoto['syncStatus']): string {
  switch (status) {
    case 'PENDING': return 'rgb(var(--warn) / 0.6)'
    case 'SYNCING': return 'rgb(var(--info) / 0.6)'
    case 'SYNCED': return 'rgb(var(--ok) / 0.6)'
    case 'FAILED':
    case 'REJEITADA': return 'rgb(var(--bad) / 0.6)'
  }
}

function statusBorderCls(status: OutFoto['syncStatus']): string {
  switch (status) {
    case 'FAILED':
      return 'border-l-2 border-l-bad/30'
    default:
      return ''
  }
}
